// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@adl/App/Components/Notify/Notify";
import { getSettingsCollection } from "@adl/App/Store/Selectors/SettingsSelectors";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@adl/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@adl/Overrides/Components/ProductPrice";
import CartLineQuantity, { CartLineQuantityStyles } from "@adl/Overrides/Widgets/Cart/CartLineQuantity";
import Remove from "@adl/XDL/Icons/Remove";
import { color, ellipsis } from "@adl/XDL/Utilities";
import { xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    hideProductPriceTooltip: getSettingsCollection(state).customSettings?.hideProductPriceTooltip,
});

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    leftSideWrapper?: InjectableCss;
    rightSideWrapper?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    brand?: LinkPresentationProps;
    lineDescription?: LinkPresentationProps;
    productInfoWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    qtyAndRemoveWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    extendedPriceWrapper?: InjectableCss;
    basicProductPriceStyles?: ProductPriceStyles;
    extendedNetPriceValue?: TypographyProps;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
}

const miniCartLineStyles: MiniCartLineStyles = {
    cartLine: {
        css: css`
            display: flex;
            position: relative;
            margin-bottom: 22px;
            &:hover {
                cursor: pointer;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 72px;
            height: 72px;
            overflow: hidden;
            margin-right: 12px;
            flex-shrink: 0;
            ${xScreen("lte", "sm")} {
                margin-right: 4px;
            }
        `,
    },
    cartLineImage: {
        width: "72px",
        height: "72px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${xScreen("lte", "sm")} {
                width: 100%;
                max-width: initial;
            }
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
        `,
    },
    rightSideWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            margin-left: 18px;
            ${({ hideProductPriceTooltip }: any) =>
                hideProductPriceTooltip ? "width: auto;" : " width: 242px;"}
        `,
    },
    productInfoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
        `,
    },
    partNumbersStyles: {
        container: {
            css: css`
                margin: 0 5px 0 0;
                flex: 1 0 auto;
                min-width: 174px;
                max-width: 100%;
            `,
        },
        erpNumberLabelText: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                &:after {
                    content: ":";
                    margin-right: 5px;
                }
            `,
        },
        erpNumberValueText: {
            size: 12,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    brand: {
        typographyProps: {
            size: 12,
            weight: "normal",
            css: css`
                letter-spacing: 0.5px;
                line-height: 13px;
                ${ellipsis(1)}
            `,
        },
        css: css`
            margin-right: 10px;
        `,
    },
    lineDescription: {
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 13px;
                text-decoration: none;
                ${ellipsis(2)}
            `,
        },
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-end;
            width: 100%;
        `,
    },
    qtyAndRemoveWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            margin-right: 18px;
        `,
    },
    cartLineQuantityStyles: {
        editableQuantityTextField: {
            sizeVariant: "small",
            labelProps: {
                css: css`
                    display: none;
                `,
            },
            cssOverrides: {
                formField: css`
                    width: auto;
                `,
                inputSelect: css`
                    width: 74px;
                    text-align: center;
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 0;
                    line-height: 18px;
                `,
            },
        },
    },
    extendedPriceWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            flex: 1;
            & > section > div {
                flex-wrap: wrap;
            }
        `,
    },
    basicProductPriceStyles: {
        price: {
            priceText: {
                size: 16,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 17px;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    weight: "normal",
                    size: 12,
                    css: css`
                        letter-spacing: 0;
                        line-height: 18px;
                    `,
                },
            },
        },
    },
    removeCartLineLink: {
        typographyProps: {
            size: 10,
            weight: 600,
            css: css`
                letter-spacing: 0;
                line-height: 11px;
                display: flex;
                flex-direction: column;
                align-items: center;
                span {
                    color: ${color("danger", 0.75)};
                }
                > ${IconWrapper} {
                    margin-bottom: 5px;
                    color: ${color("danger", 0.75)};
                }
            `,
        },
        css: css`
            margin-top: 15px;
            &:hover {
                span {
                    color: ${color("danger")};
                    ${IconWrapper} {
                        color: ${color("danger")};
                    }
                }
            }
        `,
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div:not(:last-child) {
                margin-bottom: 15px;
            }
        `,
    },
    leftSideWrapper: {
        css: css`
            width: 234px;
        `,
    },
};

const styles = miniCartLineStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const MiniCartLineCard = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
    hideProductPriceTooltip,
}: Props) => {
    
    const [showRemove, setShowRemove] = useState(false);
    const handleToggleRemove: React.MouseEventHandler<HTMLDivElement> = event => {
        event.persist();
        if (event.type !== "mouseleave" && event.type !== "mouseenter") {
            return;
        }
        setShowRemove(event.type === "mouseenter");
    };
    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper
                {...styles.cartLine}
                data-test-selector="cartLine"
                onMouseEnter={handleToggleRemove}
                onMouseLeave={handleToggleRemove}
            >
                <StyledWrapper {...styles.imageWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy}>
                        <LazyImage
                            src={cartLine.smallImagePath}
                            altText={cartLine.altText}
                            {...styles.cartLineImage}
                            {...{ transformations: { width: 72 } }}
                        />
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    <StyledWrapper {...styles.contentWrapper}>
                        <StyledWrapper {...styles.leftSideWrapper} className="miniCart-leftSide">
                            <Link href={cartLine.productUri} onClick={hideTippy} {...styles.lineDescription}>
                                {cartLine.shortDescription}
                            </Link>
                            <StyledWrapper {...styles.productInfoWrapper}>
                                <ProductPartNumbers
                                    manufacturerItem={cartLine.manufacturerItem}
                                    productNumber={cartLine.erpNumber}
                                    brand={cartLine.brand}
                                    extendedStyles={styles.partNumbersStyles}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper
                            className="miniCart-rightSide"
                            {...styles.rightSideWrapper}
                            {...{ hideProductPriceTooltip }}
                        >
                            <StyledWrapper {...styles.qtyAndPriceWrapper}>
                                <StyledWrapper {...styles.qtyAndRemoveWrapper}>
                                    <CartLineQuantity
                                        cart={cart as Cart}
                                        editable={true}
                                        extendedStyles={styles.cartLineQuantityStyles}
                                        onQtyOrderedChange={(inputQty: number) => {
                                            qtyOrderedChangeHandler && qtyOrderedChangeHandler(cartLine, inputQty);
                                        }}
                                        showActions={true}
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.extendedPriceWrapper}>
                                    {!cart?.cartNotPriced && (
                                        <ProductPrice
                                            showLabel={false}
                                            product={cartLine}
                                            extendedStyles={styles.basicProductPriceStyles}
                                        />
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper
                                css={css`
                                    visibility: ${showRemove ? "visible" : "hidden"};
                                `}
                            >
                                <Link
                                    onClick={() => removeCartLineClickHandler(cartLine)}
                                    {...styles.removeCartLineLink}
                                >
                                    <Icon src={Remove} size={12} />
                                    <Typography>{translate("Remove")}</Typography>
                                </Link>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>

                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default connect(mapStateToProps)(MiniCartLineCard);
